import { useState, useEffect } from 'react';
import { Box, VStack, StackSeparator } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import ItemCard from './elements/item-card';
import CollectionDrawer from './collection-drawer';
import ShareCollection from './share-collection';
import api from '@/utils/api';
import { Collection } from '@/lib/types';

interface Props {
  collections: Collection[];
  collectionTypeId: string;
  workspaceId: string;
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  reloadCollections: () => void;
}

export default function CollectionList({
  collections,
  collectionTypeId,
  workspaceId,
  lastContentElementRef,
  reloadCollections,
}: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<Collection | null>(null);
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shareCollectionModal, setShareCollectionModal] = useState(false);
  const itemDrawer = useModalToggle();
  const navigate = useNavigate();

  const [deletedCollectionIds, setDeletedCollectionIds] = useState<number[]>(() => {
    const stored = localStorage.getItem('deletedCollectionIds');
    return stored ? JSON.parse(stored) : [];
  });
  const filteredCollections = collections.filter(
    (collection) => !deletedCollectionIds.includes(collection.id),
  );

  useEffect(() => {
    localStorage.setItem('deletedCollectionIds', JSON.stringify(deletedCollectionIds));

    // Cleanup old deleted IDs after 1 hour to prevent localStorage bloat
    const now = Date.now();
    const storedTimestamps = JSON.parse(
      localStorage.getItem('deletedCollectionTimestamps') || '{}',
    );

    const updatedTimestamps = { ...storedTimestamps };
    deletedCollectionIds.forEach((id) => {
      if (!storedTimestamps[id]) {
        updatedTimestamps[id] = now;
      }
    });

    localStorage.setItem('deletedCollectionTimestamps', JSON.stringify(updatedTimestamps));

    // Clean up IDs older than 1 hour
    const oneHourAgo = now - 60 * 60 * 1000;
    const idsToKeep = Object.entries(updatedTimestamps as Record<string, number>).reduce(
      (acc, [id, timestamp]) => (timestamp >= oneHourAgo ? [...acc, Number(id)] : acc),
      [] as number[],
    );

    if (idsToKeep.length !== deletedCollectionIds.length) {
      setDeletedCollectionIds(idsToKeep);
    }
  }, [deletedCollectionIds]);

  const handleView = (item: Collection) => {
    setReferenceId(item.reference_id);
    itemDrawer.open();
  };

  function handleEdit(item: Collection) {
    navigate(`/${workspaceId}/collection-types/${collectionTypeId}/collections/${item.id}/edit`);
  }

  function handleDelete(item: Collection) {
    setSelectedItemId(item.id);
    setShowDeleteModal(true);
  }

  async function onDelete() {
    setIsLoading(true);
    try {
      await api.delete(`collections/${selectedItemId}`);
      // Add the deleted ID to our tracking array
      if (selectedItemId) {
        setDeletedCollectionIds((prev) =>
          prev.includes(selectedItemId) ? prev : [...prev, selectedItemId],
        );
      }
      reloadCollections();
      setShowDeleteModal(false);
      setSelectedItemId(null);
      toaster.create({ title: 'Collection removed', type: 'success' });
      setIsLoading(false);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete collection',
      });
      setIsLoading(false);
    }
  }

  async function handleGeneratePublicLink(item: Collection) {
    setShareCollectionModal(true);
    setSelectedItem(item);
  }

  async function handleDuplicate(item: Collection) {
    try {
      const promise = api.post(`collections/${item.id}/duplicate`);

      toaster.promise(promise, {
        success: {
          title: 'The collection was duplicated successfully',
        },
        error: {
          title: 'Failed to duplicate collection',
        },
        loading: { title: 'Duplicating...', description: 'Please wait' },
      });
      reloadCollections();
      await promise;

      setTimeout(() => {
        reloadCollections();
      }, 2000);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to duplicate collection',
      });
    }
  }

  async function handeChangeStatus(item: Collection, status: 'draft' | 'published') {
    try {
      const promise = api.patch(`/collections/status/${item.id}`, {
        status: status,
      });

      toaster.promise(promise, {
        success: {
          title: `The collection was ${status === 'published' ? 'shared to' : 'hidden from'} workspace`,
        },
        error: {
          title: `Failed to ${status === 'published' ? 'share to' : 'hide from'} workspace`,
        },
        loading: {
          title: `${status === 'published' ? 'Sharing to workspace' : 'Hiding from workspace'}...`,
          description: 'Please wait',
        },
      });

      reloadCollections();
      setTimeout(() => {
        reloadCollections();
      }, 3000);
      await promise;
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change status',
      });
      setIsLoading(false);
    }
  }

  return (
    <Box mt={3}>
      <VStack separator={<StackSeparator borderColor={'gray.100'} />} gap={2} align={'stretch'}>
        {filteredCollections.map((item, index) => (
          <ItemCard
            key={`index-${index}-${item.reference_id}`}
            lastContentElementRef={lastContentElementRef}
            item={item}
            onView={handleView}
            onGeneratePiblicLink={handleGeneratePublicLink}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onChangeStatus={handeChangeStatus}
          />
        ))}
      </VStack>

      {showDeleteModal && (
        <DestructiveModal
          isOpen={showDeleteModal}
          onCancel={() => setShowDeleteModal(!showDeleteModal)}
          onConfirm={() => onDelete()}
          isLoading={isLoading}
          title={'Delete Content'}
          children={`You are about to delete collection. Press 'Confirm' to continue.`}
          confirmText={'Confirm'}
        />
      )}

      {referenceId && <CollectionDrawer {...itemDrawer.props} referenceId={referenceId} />}

      {shareCollectionModal && selectedItem && (
        <ShareCollection
          isOpen={shareCollectionModal}
          onClose={() => setShareCollectionModal(!shareCollectionModal)}
          item={selectedItem}
          reloadCollections={reloadCollections}
        />
      )}
    </Box>
  );
}
